import { useEffect, useRef, useState } from "react";
import { coachApi } from "@/api";
import type { CoachNote } from "@/lib/types";
import { useUI } from "@/lib/theme";

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

function VisibilityBadge({ visibility, t }: { visibility: "private" | "shared"; t: (k: string) => string }) {
  return visibility === "shared" ? (
    <span className="rounded px-1.5 py-0.5 text-[10px] font-semibold bg-accent/15 text-accent">
      {t("notes_shared_badge")}
    </span>
  ) : (
    <span className="rounded px-1.5 py-0.5 text-[10px] font-semibold bg-muted/20 text-muted">
      {t("notes_private_badge")}
    </span>
  );
}

// ---- NoteCard (editar inline) ----
function NoteCard({
  note,
  athleteId,
  onUpdated,
  onDeleted,
}: {
  note: CoachNote;
  athleteId: string;
  onUpdated: (n: CoachNote) => void;
  onDeleted: (id: string) => void;
}) {
  const { t } = useUI();
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(note.note);
  const [vis, setVis] = useState<"private" | "shared">(note.visibility);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const ta = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (editing) ta.current?.focus();
  }, [editing]);

  const handleSave = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await coachApi.updateNote(athleteId, note.id, { note: text.trim(), visibility: vis });
      onUpdated({ ...note, note: text.trim(), visibility: vis, updated_at: new Date().toISOString() });
      setEditing(false);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    setBusy(true);
    try {
      await coachApi.deleteNote(athleteId, note.id);
      onDeleted(note.id);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-line bg-panel p-3">
      {editing ? (
        <>
          <textarea
            ref={ta}
            rows={4}
            className="mb-2 w-full resize-none rounded-lg border border-line bg-panel2 p-2 text-sm outline-none focus:border-accent"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <div className="flex items-center gap-2 flex-wrap">
            <select
              className="rounded-lg border border-line bg-panel2 px-2 py-1 text-xs outline-none focus:border-accent"
              value={vis}
              onChange={(e) => setVis(e.target.value as "private" | "shared")}
            >
              <option value="private">{t("notes_visibility_private")}</option>
              <option value="shared">{t("notes_visibility_shared")}</option>
            </select>
            <button
              onClick={() => void handleSave()}
              disabled={busy || !text.trim()}
              className="rounded-lg border border-accent/40 bg-accent/10 px-3 py-1 text-xs font-semibold text-accent transition hover:bg-accent/20 disabled:opacity-50"
            >
              {t("notes_save")}
            </button>
            <button
              onClick={() => { setEditing(false); setText(note.note); setVis(note.visibility); }}
              className="rounded-lg border border-line px-3 py-1 text-xs font-medium text-muted transition hover:text-fg"
            >
              {t("notes_cancel")}
            </button>
            {confirmDelete ? (
              <div className="ml-auto flex items-center gap-1.5">
                <span className="text-xs text-rose-500">{t("notes_delete_confirm")}</span>
                <button
                  onClick={() => void handleDelete()}
                  disabled={busy}
                  className="rounded-lg bg-rose-500 px-2 py-1 text-xs font-semibold text-white disabled:opacity-60 hover:bg-rose-600"
                >
                  {t("notes_delete")}
                </button>
                <button
                  onClick={() => setConfirmDelete(false)}
                  className="rounded-lg border border-line px-2 py-1 text-xs text-muted hover:text-fg"
                >
                  {t("notes_cancel")}
                </button>
              </div>
            ) : (
              <button
                onClick={() => setConfirmDelete(true)}
                className="ml-auto rounded-lg border border-rose-500/30 bg-rose-500/10 px-2 py-1 text-xs font-medium text-rose-600 transition hover:bg-rose-500/20 dark:text-rose-400"
              >
                {t("notes_delete")}
              </button>
            )}
          </div>
        </>
      ) : (
        <button
          onClick={() => setEditing(true)}
          className="w-full text-left"
          title="Click para editar"
        >
          <div className="flex items-start justify-between gap-2 mb-1">
            <VisibilityBadge visibility={note.visibility} t={t} />
            <span className="text-[11px] text-muted">{formatDate(note.updated_at)}</span>
          </div>
          <p className="whitespace-pre-wrap text-sm text-fg">{note.note}</p>
        </button>
      )}
    </div>
  );
}

// ---- Formulario de nueva nota ----
function NewNoteForm({
  athleteId,
  onAdded,
}: {
  athleteId: string;
  onAdded: (n: CoachNote) => void;
}) {
  const { t } = useUI();
  const [text, setText] = useState("");
  const [vis, setVis] = useState<"private" | "shared">("private");
  const [busy, setBusy] = useState(false);

  const handleAdd = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { note } = await coachApi.createNote(athleteId, { note: text.trim(), visibility: vis });
      onAdded(note);
      setText("");
      setVis("private");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-line bg-panel2/60 p-3">
      <textarea
        rows={3}
        className="mb-2 w-full resize-none rounded-lg border border-line bg-panel p-2 text-sm outline-none focus:border-accent"
        placeholder={t("notes_new_placeholder")}
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <div className="flex items-center gap-2">
        <select
          className="rounded-lg border border-line bg-panel px-2 py-1 text-xs outline-none focus:border-accent"
          value={vis}
          onChange={(e) => setVis(e.target.value as "private" | "shared")}
        >
          <option value="private">{t("notes_visibility_private")}</option>
          <option value="shared">{t("notes_visibility_shared")}</option>
        </select>
        <button
          onClick={() => void handleAdd()}
          disabled={busy || !text.trim()}
          className="rounded-lg bg-accent px-3 py-1.5 text-xs font-semibold text-white transition enabled:hover:brightness-110 disabled:opacity-50"
        >
          {t("notes_add")}
        </button>
      </div>
    </div>
  );
}

// ---- Componente principal ----
export default function CoachNotes({ athleteId }: { athleteId: string }) {
  const { t } = useUI();
  const [notes, setNotes] = useState<CoachNote[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    coachApi
      .notes(athleteId)
      .then((r) => setNotes(r.notes))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [athleteId]);

  const handleAdded = (n: CoachNote) => setNotes((prev) => [n, ...prev]);
  const handleUpdated = (n: CoachNote) => setNotes((prev) => prev.map((x) => (x.id === n.id ? n : x)));
  const handleDeleted = (id: string) => setNotes((prev) => prev.filter((x) => x.id !== id));

  return (
    <div className="space-y-4">
      <NewNoteForm athleteId={athleteId} onAdded={handleAdded} />

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="h-20 animate-pulse rounded-xl bg-panel2" />
          ))}
        </div>
      ) : notes.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted">{t("notes_empty")}</p>
      ) : (
        <div className="space-y-2">
          {notes.map((n) => (
            <NoteCard
              key={n.id}
              note={n}
              athleteId={athleteId}
              onUpdated={handleUpdated}
              onDeleted={handleDeleted}
            />
          ))}
        </div>
      )}
    </div>
  );
}
